'use client';
import {
  Badge,
  Box,
  Checkbox,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Typography,
  FormControlLabel,
  IconButton,
  RadioGroup,
  Radio,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useRouter } from 'next/navigation';
import { MaterialFilterDialog } from './MaterialFilterDialog';
import {
  useProjectFilterStore,
  defaultProjectFilterCriteria,
  hasProjectExtendedFiltersSet,
} from '@/stores/projectFilterStore';
import { log } from '@/lib/utils/logger';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { useDialogStore } from '@/stores/dialogStore';
import { OperationEnum, PrivilegeEnum, type ProjectSearchCriteria } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { usePrivileges } from '@/hooks/usePrivileges';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { useBackend } from '@/hooks/useBackend';
import { useSystemConfig } from '@/hooks/useSystemConfig';

import { PiGeographyFilters } from '@/components/common/PiGeographyFilters';
import { useReopenSearchPanelOnFailure } from '@/hooks/useReopenSearchPanelOnFailure';
import { markNextSearchUserInitiated } from '@/stores/searchUiStore';

const OPTION_KEYS: OptionKey[] = [
  'synthesisTechniques',
  'projectMaterials',
  'status',
  'projectType',
  'projectSearchStatus',
];

interface ProjectSearchPanelProps {
  /** Custom title for the search panel */
  title?: string;
  /** Whether this panel is used in a selection dialog (hides action buttons) */
  isSelectionMode?: boolean;
  /** Optional controlled filters (for selection dialogs to avoid updating shared store) */
  filters?: ProjectSearchCriteria;
  /** Callback when filters change (for selection dialogs) */
  onFiltersChange?: (filters: ProjectSearchCriteria) => void;
  /** Optional controlled pagination (for selection dialogs) */
  paginationModel?: { page: number; pageSize: number };
  /** Callback when pagination changes (for selection dialogs) */
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
}

export const ProjectSearchPanel = ({
  title,
  isSelectionMode = false,
  filters: controlledFilters,
  onFiltersChange,
  paginationModel: controlledPaginationModel,
  onPaginationModelChange,
}: ProjectSearchPanelProps = {}) => {
  const storeState = useProjectFilterStore();
  const {
    setFilters,
    resetFilters,
    setPaginationModel,
    filters: storeFilters,
    isSearching,
    setIsSearching,
  } = storeState;
  const { options, loading, error } = useSelectOptions(OPTION_KEYS);
  const { isAuthorized } = usePrivileges();
  const backend = useBackend();
  const systemConfig = useSystemConfig();
  const router = useRouter();
  const hasGlobalSampleCreate = isAuthorized(PrivilegeEnum.Sample, 'create');
  const [hasProjectSampleCreate, setHasProjectSampleCreate] = useState(false);
  const canAddSample = hasGlobalSampleCreate || hasProjectSampleCreate || canCreateWithoutProject(isAuthorized);

  const isControlled = controlledFilters !== undefined;

  /** Unsaved edits when using the persisted store. `null` = follow `storeFilters`. Controlled dialogs push on each change (no mirror effect). */
  const [filterDraft, setFilterDraft] = useState<ProjectSearchCriteria | null>(null);
  const localFilters =
    isControlled && controlledFilters !== undefined ? controlledFilters : (filterDraft ?? storeFilters);

  const setLocalFilters = useCallback(
    (next: ProjectSearchCriteria | ((prev: ProjectSearchCriteria) => ProjectSearchCriteria)) => {
      if (isControlled && onFiltersChange) {
        const base = controlledFilters ?? defaultProjectFilterCriteria;
        const merged =
          typeof next === 'function' ? (next as (p: ProjectSearchCriteria) => ProjectSearchCriteria)(base) : next;
        onFiltersChange(merged);
        onPaginationModelChange?.({ page: 0, pageSize: controlledPaginationModel?.pageSize ?? 25 });
        return;
      }
      setFilterDraft((prevDraft) => {
        const base = prevDraft ?? useProjectFilterStore.getState().filters;
        return typeof next === 'function' ? (next as (p: ProjectSearchCriteria) => ProjectSearchCriteria)(base) : next;
      });
    },
    [isControlled, onFiltersChange, onPaginationModelChange, controlledPaginationModel?.pageSize, controlledFilters]
  );

  // Start expanded in selection mode (for dialogs), collapsed by default in main table
  const [isExpanded, setIsExpanded] = useState(isSelectionMode);
  useReopenSearchPanelOnFailure(setIsExpanded);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [createSampleAnchorEl, setCreateSampleAnchorEl] = useState<null | HTMLElement>(null);
  const createSampleMenuOpen = Boolean(createSampleAnchorEl);
  const {
    showAddProjectDialog,
    showAddToInventoryDialog,
    showCombineStartingMaterialsDialog,
    showMultiSampleGrowthDialog,
  } = useDialogStore();

  // Material options for filtering
  const materialOptions = options.projectMaterials
    ?.filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
    .sort((a, b) => a.name!.localeCompare(b.name!))
    .map((m) => ({
      id: m.id!,
      name: m.name!,
    }));

  const handleApply = useCallback(() => {
    markNextSearchUserInitiated();
    setIsSearching(true);
    log.debug('[ProjectSearchPanel] Applying project filters:', localFilters);
    if (isControlled && onFiltersChange) {
      onFiltersChange(localFilters);
      if (onPaginationModelChange) {
        onPaginationModelChange({ page: 0, pageSize: controlledPaginationModel?.pageSize ?? 25 });
      }
    } else {
      setFilters(localFilters);
      setFilterDraft(null);
      setPaginationModel((prevModel) => ({
        ...prevModel,
        page: 0,
      }));
    }
    setIsExpanded(false);
  }, [
    localFilters,
    isControlled,
    onFiltersChange,
    onPaginationModelChange,
    controlledPaginationModel,
    setFilters,
    setPaginationModel,
    setIsSearching,
  ]);

  // Show add-to-inventory buttons if user has Sample create globally or on at least one project.
  useEffect(() => {
    if (hasGlobalSampleCreate) return;
    let cancelled = false;
    backend.project
      .hasAnyProjectWithPrivilege('Sample', OperationEnum.Create)
      .then((has) => {
        if (!cancelled) setHasProjectSampleCreate(has);
      })
      .catch(() => {
        if (!cancelled) setHasProjectSampleCreate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasGlobalSampleCreate, backend.project]);

  const handleReset = useCallback(() => {
    if (isControlled && onFiltersChange) {
      onFiltersChange(defaultProjectFilterCriteria);
      if (onPaginationModelChange) {
        onPaginationModelChange({ page: 0, pageSize: controlledPaginationModel?.pageSize ?? 25 });
      }
    } else {
      resetFilters();
      setFilterDraft(null);
    }
  }, [isControlled, onFiltersChange, onPaginationModelChange, controlledPaginationModel, resetFilters]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (isSearching) return;
      handleApply();
    },
    [handleApply, isSearching]
  );

  const handleAddProject = async () => {
    try {
      const project = await showAddProjectDialog();
      if (project) {
        log.trace('[ProjectSearchPanel] Project added:', project);
        const label = project.projectLabel;
        if (label) {
          const basePath = systemConfig?.basePath ?? '';
          router.push(`${basePath}/projects/${encodeURIComponent(label)}`);
        }
      }
    } catch (error) {
      log.error('[ProjectSearchPanel] Failed to add project:', error);
    }
  };

  const handleAddToInventory = async () => {
    try {
      const result = await showAddToInventoryDialog();
      if (result) {
        log.trace('[ProjectSearchPanel] Added to inventory:', result.samples);
        if (result.samples.length === 1) {
          const label = result.samples[0]?.sampleLabel;
          if (label) {
            const basePath = systemConfig?.basePath ?? '';
            router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
          }
        }
      }
    } catch (error) {
      log.error('[ProjectSearchPanel] Failed to add to inventory:', error);
    }
  };

  const handleAddSampleFromPrecursors = async () => {
    setCreateSampleAnchorEl(null);
    try {
      const result = await showCombineStartingMaterialsDialog();
      if (result) {
        log.trace('[ProjectSearchPanel] Added sample from startingMaterials:', result);
        const label = result.sampleLabel;
        if (label) {
          const basePath = systemConfig?.basePath ?? '';
          router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
        }
      }
    } catch (error) {
      log.error('[ProjectSearchPanel] Failed to add sample from startingMaterials:', error);
    }
  };

  const handleAddMultiSampleGrowth = async () => {
    setCreateSampleAnchorEl(null);
    try {
      const result = await showMultiSampleGrowthDialog();
      if (result) {
        log.trace('[ProjectSearchPanel] Multi-sample growth run created:', result);
        const label = result.sampleLabel;
        if (label) {
          const basePath = systemConfig?.basePath ?? '';
          router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
        }
      }
    } catch (error) {
      log.error('[ProjectSearchPanel] Failed to open multi-sample growth dialog:', error);
    }
  };

  const canAddProject =
    isAuthorized(PrivilegeEnum.UserProject, 'create') || isAuthorized(PrivilegeEnum.InternalProject, 'create');

  if (loading) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1 }}>
        <Typography variant="body1">Loading project filter options...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="body1">Error loading project filter options...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" alignItems="center" gap={2} sx={{ mt: isSelectionMode ? 1 : 2, mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          {title || 'Search Projects'}
        </Typography>
        {!isSelectionMode ? (
          <Box display="flex" gap={1} sx={{ ml: 'auto' }}>
            {canAddSample ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => setCreateSampleAnchorEl(e.currentTarget)}
                  size="medium"
                  sx={{ fontWeight: 500, minWidth: 160 }}
                  endIcon={<ArrowDropDown />}
                >
                  Add Sample
                </Button>
                <Menu
                  anchorEl={createSampleAnchorEl}
                  open={createSampleMenuOpen}
                  onClose={() => setCreateSampleAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleAddToInventory}>
                    <ListItemIcon>
                      <InventoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add to Inventory</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleAddSampleFromPrecursors}>
                    <ListItemIcon>
                      <InventoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Combine Starting Materials</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleAddMultiSampleGrowth}>
                    <ListItemIcon>
                      <InventoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Multi-Sample Growth</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            {canAddProject ? (
              <Button
                variant="outlined"
                onClick={handleAddProject}
                size="medium"
                sx={{
                  fontWeight: 500,
                }}
                startIcon={<AddIcon />}
              >
                Add Project
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {/* Quick search row + compact filter toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
          mt: 0.5,
        }}
        onKeyDown={handleSearchOnEnter}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            label="Search in Title, ID, Collaborators"
            variant="outlined"
            size="small"
            value={localFilters.searchText || ''}
            onChange={(e) => setLocalFilters({ ...localFilters, searchText: e.target.value })}
            sx={{ flex: 1, minWidth: 260 }}
            placeholder="Search by project name, ID, collaborators…"
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={localFilters.type || ''}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  type: e.target.value || undefined,
                  includeInternal: e.target.value ? undefined : localFilters.includeInternal,
                  includeExternal: e.target.value ? undefined : localFilters.includeExternal,
                })
              }
              label="Project Type"
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {options.projectType?.map((option) => (
                <MenuItem key={option.code || option.id} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!localFilters.ownProjectsOnly}
                onChange={(e) => setLocalFilters({ ...localFilters, ownProjectsOnly: e.target.checked })}
                color="primary"
              />
            }
            label="My projects"
          />
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={!hasProjectExtendedFiltersSet(localFilters)}
            sx={{ '& .MuiBadge-badge': { right: 8, top: 8 } }}
          >
            <Button
              variant={isExpanded ? 'contained' : 'outlined'}
              size="small"
              startIcon={<FilterList />}
              onClick={() => setIsExpanded((prev) => !prev)}
              sx={{ minWidth: 90, whiteSpace: 'nowrap' }}
            >
              Filter
            </Button>
          </Badge>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: 120 }}
            size="small"
            disabled={isSearching}
            startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isSearching ? 'Searching…' : 'Search'}
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ width: 90 }} size="small" color="error">
            Reset
          </Button>
        </Box>
      </Box>

      {isExpanded ? (
        <Box sx={{ width: '100%', mb: isSelectionMode ? 1 : 2, pt: 1, pb: 1 }}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={isSelectionMode ? 1 : 2}
            mb={isSelectionMode ? 2 : 3}
            alignItems="center"
          >
            {/* Material MultiSelect */}
            <Box display="flex" alignItems="center" gap={1}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                options={materialOptions ?? []}
                getOptionLabel={(option) => option.name}
                filterOptions={(opts, params) => filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={(() => {
                  // If elements are selected, show "Element(s) + *" instead of individual materials
                  if (localFilters.elements && localFilters.elements.length > 0) {
                    const elementDisplay = localFilters.elements.join(', ');
                    return [
                      {
                        id: -1, // Special ID for element display
                        name: `${elementDisplay} + *`,
                      },
                    ];
                  }
                  // Otherwise show selected materials
                  return materialOptions?.filter((m) => (localFilters.materials || []).includes(m.id)) ?? [];
                })()}
                onChange={(_, selectedOptions) => {
                  // If user selects/deselects, clear elements and use materials
                  setLocalFilters({
                    ...localFilters,
                    materials: selectedOptions.map((opt) => opt.id),
                    elements: [],
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material"
                    size="small"
                    sx={{ minWidth: 200 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <IconButton size="small" onClick={() => setIsMaterialDialogOpen(true)} sx={{ ml: 1 }}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Technique dropdown */}
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={options.synthesisTechniques ?? []}
              getOptionLabel={(option) => option?.name ?? ''}
              value={options.synthesisTechniques?.find((t) => t.id === localFilters.synTechniqueId) ?? null}
              onChange={(_, newValue) => setLocalFilters({ ...localFilters, synTechniqueId: newValue?.id ?? '' })}
              renderInput={(params) => <TextField {...params} label="Technique" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Project Status</InputLabel>
              <Select
                value={localFilters.status ?? ''}
                onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value || undefined })}
                label="Project Status"
              >
                <MenuItem value="">
                  <em>All Statuses</em>
                </MenuItem>
                {options.projectSearchStatus?.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>Year:</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={localFilters.year || ''}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      year: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  sx={{ minWidth: 100 }}
                />
              </Box>
            </LocalizationProvider>

            <FormControl>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Project Classification:
              </Typography>
              <RadioGroup
                row
                value={
                  localFilters.includeInternal === true
                    ? 'internal'
                    : localFilters.includeExternal === true
                      ? 'external'
                      : 'all'
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setLocalFilters({
                    ...localFilters,
                    includeInternal: value === 'internal' ? true : undefined,
                    includeExternal: value === 'external' ? true : undefined,
                    // Clear specific type when classification is selected
                    type: value !== 'all' ? undefined : localFilters.type,
                  });
                }}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="internal" control={<Radio size="small" />} label="Internal" />
                <FormControlLabel value="external" control={<Radio size="small" />} label="External" />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={isSelectionMode ? 1 : 2}
            alignItems="center"
            sx={{ width: '100%', mt: isSelectionMode ? 1 : 1.5 }}
          >
            <PiGeographyFilters
              state={localFilters.state}
              institution={localFilters.institution}
              onStateChange={(s) => setLocalFilters({ ...localFilters, state: s ?? '' })}
              onInstitutionChange={(i) => setLocalFilters({ ...localFilters, institution: i ?? '' })}
            />
          </Box>

          {/* Material Filter Dialog */}
          <MaterialFilterDialog
            open={isMaterialDialogOpen}
            onClose={() => setIsMaterialDialogOpen(false)}
            onApply={(selectedIds, selectedElements) => {
              setLocalFilters({
                ...localFilters,
                materials: selectedIds,
                elements: selectedElements,
              });
            }}
            selectedMaterialIds={localFilters.materials ?? []}
            selectedElements={localFilters.elements ?? []}
            allMaterials={materialOptions ?? []}
          />
        </Box>
      ) : null}
    </Box>
  );
};
